/**
 * The one formatter for a heat-grid figure (plots plan Q).
 *
 * Python rounds once, half away from zero, and stamps each column's decimals; this
 * prints exactly those decimals (`roundHalfAwayFromZeroAt` first, so `Intl` never
 * re-rounds half up) and groups them in the request locale. A change column
 * is signed with a typographic minus, and so is an NPS score ("+25", "−5",
 * "0": owner decision 2026-10-01); a share prints its percent sign. An empty
 * cell prints nothing the reader could take for a zero.
 */

import { HeatGridColumn } from '@digital-twins/slide-schemas/generated/plot-types'

import { roundHalfAwayFromZeroAt } from '../../foundations/producer-rounding'

export const HEAT_GRID_FALLBACK_LOCALE = 'en'
const MINUS_SIGN = '−'

export type HeatValueFormat = Pick<HeatGridColumn, 'valueFormat' | 'decimals'>

function safeLocale(locale: string | null | undefined): string {
  return typeof locale === 'string' && locale.trim() ? locale.trim() : HEAT_GRID_FALLBACK_LOCALE
}

function formatNumber(value: number, decimals: number, locale: string, signed: boolean): string {
  const rounded = roundHalfAwayFromZeroAt(value, decimals)
  const options: Intl.NumberFormatOptions = {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    signDisplay: signed ? 'exceptZero' : 'auto',
  }
  let text: string
  try {
    text = new Intl.NumberFormat(locale, options).format(rounded === 0 ? 0 : rounded)
  } catch {
    text = new Intl.NumberFormat(HEAT_GRID_FALLBACK_LOCALE, options).format(rounded === 0 ? 0 : rounded)
  }
  return text.replace(/^-/, MINUS_SIGN)
}

/** A cell's printed figure, or null for an empty cell. */
export function formatHeatValue(
  value: number | null | undefined,
  column: HeatValueFormat,
  locale?: string | null,
): string | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return null
  }
  const lang = safeLocale(locale)
  const decimals = Math.max(0, Math.min(3, column.decimals ?? 1))
  const signed = column.valueFormat === 'points' || column.valueFormat === 'nps'
  const text = formatNumber(value, decimals, lang, signed)
  if (column.valueFormat === 'percent') {
    return `${text}%`
  }
  return text
}

/** A row or column mean, printed at the grid's widest decimals. */
export function formatHeatAverage(
  value: number | null | undefined,
  columns: ReadonlyArray<HeatValueFormat>,
  locale?: string | null,
): string | null {
  const measured = columns.filter((column) => column.valueFormat !== 'points')
  const decimals = measured.reduce((max, column) => Math.max(max, column.decimals ?? 1), 0)
  const isEvery = (format: HeatValueFormat['valueFormat']) =>
    measured.length > 0 && measured.every((column) => column.valueFormat === format)
  // A mean of NPS scores is an NPS score, signed like its cells.
  const valueFormat = isEvery('percent') ? 'percent' : isEvery('nps') ? 'nps' : 'mean'
  return formatHeatValue(value, { valueFormat, decimals }, locale)
}
